import { useEffect, useRef, useState } from "react";
import type { Rpc } from "../lib/rpc";

interface PlatformStatus {
  status?: string;
  message?: string;
  timestamp?: string;
}
interface ScriptLog { platform: string; text: string; stream: "stdout" | "stderr" }
interface ScriptState { running: boolean; startedAt?: string | null }

interface Props {
  rpc: Rpc;
  electronConnected: boolean;
  platform: string | null;
  loggedIn: boolean | undefined;
}

const TAIL_MAX = 50;

export function StatusSubBar({ rpc, electronConnected, platform, loggedIn }: Props) {
  const [status, setStatus] = useState<PlatformStatus | null>(null);
  const [running, setRunning] = useState<boolean>(false);
  const [logTail, setLogTail] = useState<ScriptLog[]>([]);
  const [open, setOpen] = useState(false);
  const tailRef = useRef<HTMLDivElement | null>(null);

  // Poll status + script state.
  useEffect(() => {
    if (!electronConnected || !platform) {
      setStatus(null);
      setRunning(false);
      setLogTail([]);
      return;
    }
    let cancelled = false;
    async function tick() {
      try {
        const [s, st] = await Promise.all([
          rpc.call<PlatformStatus>("get-status", platform),
          rpc.call<ScriptState>("get-script-state", platform),
        ]);
        if (!cancelled) {
          setStatus(s);
          setRunning(!!st?.running);
        }
      } catch { /* swallow */ }
    }
    tick();
    const handle = window.setInterval(tick, 3000);
    return () => { cancelled = true; window.clearInterval(handle); };
  }, [rpc, electronConnected, platform]);

  // Subscribe to live script-log events for THIS platform only.
  useEffect(() => {
    if (!electronConnected || !platform) {
      setLogTail([]);
      return;
    }
    const off = rpc.onEvent<ScriptLog>("script-log", (entry) => {
      if (!entry || entry.platform !== platform) return;
      setLogTail((prev) => {
        const next = prev.concat(entry);
        return next.length > TAIL_MAX ? next.slice(next.length - TAIL_MAX) : next;
      });
    });
    return off;
  }, [rpc, electronConnected, platform]);

  // Auto-scroll the tail to bottom on new entries.
  useEffect(() => {
    if (open && tailRef.current) {
      tailRef.current.scrollTop = tailRef.current.scrollHeight;
    }
  }, [logTail, open]);

  if (!platform) return null;

  // Compute the visible state for the colored bar.
  let stateKey: "login" | "off" | "idle" | "ok" | "working" | "error" | "unknown" = "unknown";
  let label = "—";
  let icon = "•";

  if (loggedIn === false && electronConnected) {
    stateKey = "login";
    icon = "⚠";
    label = "Login required";
  } else if (!running) {
    stateKey = "off";
    icon = "○";
    label = "Script not running — click Start";
  } else if (status?.status === "error") {
    stateKey = "error";
    icon = "✖";
    label = status.message || "Error";
  } else if (status?.status === "working") {
    stateKey = "working";
    icon = "⟳";
    label = status.message || "Working…";
  } else if (status?.status === "ok") {
    stateKey = "ok";
    icon = "✓";
    label = status.message || "OK";
  } else if (status?.status === "idle") {
    stateKey = "idle";
    icon = "•";
    label = status.message || "Idle";
  } else if (status?.message) {
    stateKey = "idle";
    label = status.message;
  }

  const lastLog = logTail[logTail.length - 1];

  return (
    <div className={`status-subbar state-${stateKey}`}>
      <div className="ssb-head" onClick={() => setOpen((v) => !v)}>
        <span className="ssb-icon">{icon}</span>
        <span className="ssb-label">{label}</span>
        {lastLog && (
          <span className="ssb-tail" title={lastLog.text}>
            <span className="ssb-tail-sep">›</span>
            <span className="ssb-tail-text">{lastLog.text}</span>
          </span>
        )}
        <span className="ssb-toggle">{open ? "▾ hide" : "▸ logs"}</span>
      </div>
      {open && (
        <div ref={tailRef} className="ssb-log">
          {logTail.length === 0 ? (
            <div className="muted small">No log lines yet — they appear as the worker prints output.</div>
          ) : (
            logTail.map((entry, i) => (
              <div key={i} className={`ssb-log-line stream-${entry.stream}`}>
                {entry.text}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
